import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import { getAgentHandoff, setAgentHandoff } from '../utils/aiAgentHandoff';
import { savePromptToSession } from '../utils/aiPromptStorage';
import { isCheckoutOverlayPath } from '../utils/checkoutOverlayNavigation';
import { hasPendingPostSignupJourney } from '../utils/postSignupJourney';

const SAMPLE_PROMPTS = {
  lumibot_readme_deploy:
    'Create a sample Lumibot strategy, run a supported backtest on BotSpot, compare the results, and show me how I would start a paper deployment with monitoring.',
  lumibot_deploy_sample:
    'Create a sample Lumibot strategy, run a supported backtest on BotSpot, compare the results, and show me how I would start a paper deployment with monitoring.',
};

const FORWARDED_QUERY_KEYS = [
  'source',
  'sample',
  'prompt_template',
  'handoff',
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  '$feature/google_search_signup_experience_v2_variant',
  '$feature_flag_google_search_signup_experience_v2_variant',
  '$feature/google_search_auth_first_personalization_2026_08_variant',
  '$feature_flag_google_search_auth_first_personalization_2026_08_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_signup_experience_v2_experiment_name',
  'google_search_signup_experience_v2_variant',
  'google_search_auth_first_personalization_2026_08_experiment_name',
  'google_search_auth_first_personalization_2026_08_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'postSignupCheckoutVariant',
];

export function getSalesPromptIntent(searchParams) {
  const rawPrompt = searchParams.get('prompt') || '';
  const prompt = rawPrompt.trim();
  const sample = (searchParams.get('sample') || searchParams.get('prompt_template') || '').trim();
  const handoff = (searchParams.get('handoff') || '').trim();
  const samplePrompt = SAMPLE_PROMPTS[sample] || '';

  return {
    hasIntent: Boolean(prompt || samplePrompt || handoff),
    prompt: prompt || samplePrompt,
    sample,
    handoff,
    promptTransport: handoff
      ? 'handoff_token'
      : sample
        ? 'sample_template'
        : prompt
          ? 'query'
          : null,
    promptSource: searchParams.get('utm_source') || searchParams.get('source') || 'sales',
  };
}

function mergeSearchParams(targetParams, sourceParams) {
  FORWARDED_QUERY_KEYS.forEach((key) => {
    const value = sourceParams.get(key);
    if (value && !targetParams.has(key)) {
      targetParams.set(key, value);
    }
  });
}

function getPromptPageUrl(baseParams, sourceParams) {
  const params = new URLSearchParams(baseParams);
  mergeSearchParams(params, sourceParams);
  const queryString = params.toString();
  return queryString ? `/sales-ai-prompt?${queryString}` : '/sales-ai-prompt';
}

function getSalesChooseUrl(baseParams, sourceParams) {
  const params = new URLSearchParams(baseParams);
  mergeSearchParams(params, sourceParams);
  const queryString = params.toString();
  return queryString ? `/sales-choose?${queryString}` : '/sales-choose';
}

export default function useSalesPromptRouting({ pageVariant = 'control' } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useUnifiedAuth();
  const { isPayingCustomer, isLoading: paymentLoading } = usePaymentStatus();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const showLoginHint = searchParams.get('showLogin') === '1';
  const loginReturnTo = searchParams.get('successUrl') || '/dashboard';
  const promptIntent = useMemo(() => getSalesPromptIntent(searchParams), [searchParams]);
  const newSignupReturnTo = promptIntent.hasIntent
    ? getPromptPageUrl(new URLSearchParams(), searchParams)
    : getSalesChooseUrl(new URLSearchParams(), searchParams);

  const seedPromptIntent = useCallback(() => {
    if (!promptIntent.hasIntent) return null;

    if (promptIntent.prompt) {
      savePromptToSession(promptIntent.prompt);
    }

    const handoff = getAgentHandoff() || {};
    const updatedHandoff = {
      ...handoff,
      source: promptIntent.promptSource,
      promptSource: promptIntent.promptSource,
      promptTransport: promptIntent.promptTransport,
      salesPageVariant: pageVariant,
    };

    if (promptIntent.prompt) {
      updatedHandoff.prompt = promptIntent.prompt;
    }
    if (promptIntent.sample) {
      updatedHandoff.sample = promptIntent.sample;
    }
    if (promptIntent.handoff) {
      updatedHandoff.handoffToken = promptIntent.handoff;
    }

    setAgentHandoff(updatedHandoff);
    return updatedHandoff;
  }, [pageVariant, promptIntent]);

  useEffect(() => {
    seedPromptIntent();
  }, [seedPromptIntent]);

  const initialAuthCapturedRef = useRef(false);
  const wasAuthedOnLandingRef = useRef(false);

  useEffect(() => {
    const waitingForPayment = isAuthenticated && paymentLoading;
    if (authLoading || waitingForPayment) return;

    if (!initialAuthCapturedRef.current) {
      initialAuthCapturedRef.current = true;
      wasAuthedOnLandingRef.current = isAuthenticated;
    }

    if (!wasAuthedOnLandingRef.current || !isAuthenticated) return;

    // Sales routes can remain mounted behind checkout. Their auth/payment
    // effects must not replace the active checkout URL when those values
    // refresh, including after the short post-signup routing guard expires.
    if (isCheckoutOverlayPath(window.location.pathname)) return;

    // A backend-confirmed new account owns its next transition. Do not let the
    // landing-page existing-user router bypass attribution or the experiment.
    if (hasPendingPostSignupJourney()) return;

    if (isPayingCustomer) {
      seedPromptIntent();
      navigate('/ai-agent', { replace: true });
      return;
    }

    if (promptIntent.hasIntent) {
      seedPromptIntent();
      navigate(getPromptPageUrl(new URLSearchParams(), searchParams), { replace: true });
      return;
    }

    navigate('/checkout/starter-plan?redirect=starter_plan', { replace: true });
  }, [
    authLoading,
    isAuthenticated,
    isPayingCustomer,
    navigate,
    paymentLoading,
    promptIntent.hasIntent,
    searchParams,
    seedPromptIntent,
  ]);

  return {
    showLoginHint,
    loginReturnTo,
    newSignupReturnTo,
    hasPromptIntent: promptIntent.hasIntent,
    seedPromptIntent,
  };
}
